/**
 * Return an array of the given length
 */
export const range = (x: number): number[] => {
    const arr: undefined[] = [...Array<undefined>(x)];
    const rng = arr.map((_, i) => i);
    return rng;
};

/**
 * Array filter predicate for removing null and undefined values from a list
 */
export const notEmpty = <T>(value: T | null | undefined): value is T => {
    return value !== null && value !== undefined;
};

/**
 * Array filter predicate for removing null, undefined, and empty string values from a list
 */
export const notEmptyOrBlank = <T>(
    value: T | "" | null | undefined,
): value is T => {
    return value !== "" && value !== null && value !== undefined;
};

/**
 * Array filter predicate used to make the array unique
 */
export const unique = <T>(value: T, index: number, arr: T[]) => {
    return arr.indexOf(value) === index;
};

/**
 * Extract an Object's keys and return them as an array
 *
 * Same as Object.keys, but with a slightly tighter type definition.
 * See https://github.com/Microsoft/TypeScript/pull/12253#issuecomment-393954723
 */
export const objectKeys = Object.keys as <T>(
    o: T,
) => Extract<keyof T, string>[];

/**
 * Group an array of objects by the value of an object key
 */
export const groupBy = function* <T extends object, K extends keyof T>(
    arr: T[],
    key: K,
): Generator<Array<T>, void, unknown> {
    if (arr.length <= 0) {
        return;
    }
    type KeyType = T[K];
    let lastKey: KeyType = arr[0][key];
    let currentGroup: T[] = [];
    for (const obj of arr) {
        const currKey = obj[key];
        if (currKey !== lastKey) {
            yield currentGroup;
            currentGroup = [];
        }
        currentGroup.push(obj);
        lastKey = currKey;
    }
    if (currentGroup.length > 0) {
        yield currentGroup;
    }
};

/**
 * Sort an array of objects by the value of an object key
 */
export const sortBy = <
    K extends string | symbol | number,
    T extends Record<K, unknown>,
>(
    arr: T[],
    key: K,
): T[] => {
    return arr.sort((a, b) => {
        const vA = a[key];
        const vB = b[key];
        if (vA === vB) {
            return 0;
        }
        return vA < vB ? -1 : 1;
    });
};

/**
 * Call the given function at-most once per the given duration.
 */
export const throttle = (fn: () => void, delayMS: number): (() => void) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return (): void => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(fn, delayMS);
    };
};

/**
 * Created with the intended use case of explicitly validating that dynamically
 * created key exists in a set of CSS class names.
 */
export const isValidKey = <T extends object>(
    obj: T,
    key: string | number | symbol,
): key is keyof T => {
    return key in obj;
};
